import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BoxIcon } from 'lucide-react';
export type KpiTone = 'neutral' | 'brand' | 'warn' | 'danger' | 'ok';
interface KpiCardProps {
  label: string;
  /** Omit when the platform genuinely cannot report this yet. */
  value?: number | string;
  /** What the number counts, stated so it cannot be misread. */
  meaning: string;
  icon: BoxIcon;
  tone?: KpiTone;
  to?: string;
  linkLabel?: string;
  /**
   * A comparison is only rendered when a previous period actually exists. There
   * is no default trend: an invented "↑ 12.5%" is worse than no comparison.
   */
  comparison?: {
    direction: 'up' | 'down' | 'flat';
    text: string;
  };
  /** Values for a sparkline, oldest first. Fewer than 3 renders nothing. */
  series?: number[];
}
const TONE: Record<KpiTone, {
  fill: string;
  text: string;
}> = {
  neutral: {
    fill: 'bg-canvas',
    text: 'text-ink-muted'
  },
  brand: {
    fill: 'bg-brand-50',
    text: 'text-brand-700'
  },
  warn: {
    fill: 'bg-warn-50',
    text: 'text-warn-700'
  },
  danger: {
    fill: 'bg-danger-50',
    text: 'text-danger-700'
  },
  ok: {
    fill: 'bg-ok-50',
    text: 'text-ok-700'
  }
};
export function KpiCard({
  label,
  value,
  meaning,
  icon: Icon,
  tone = 'neutral',
  to,
  linkLabel,
  comparison,
  series
}: KpiCardProps) {
  const palette = TONE[tone];
  const known = value !== undefined && value !== null;
  return <div className="flex min-w-0 flex-col rounded-card border border-line bg-surface p-4" data-id="element-426" data-mp="2jbhx9tz5qo7k">
      <div className="flex items-start justify-between gap-3" data-id="element-427" data-mp="2nctp865i7lix">
        <p className="min-w-0 text-caption font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-428" data-mp="2ncaaeyb5hd94">
          {label}
        </p>
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${palette.fill} ${palette.text}`} data-id="element-429" data-mp="29fezt1f93dqy">
          <Icon className="h-4 w-4" aria-hidden="true" data-id="element-430" data-mp="xwuam9j493vw" />
        </span>
      </div>

      <p className="mt-2.5 text-h2 font-semibold leading-none tracking-tight text-ink" data-id="element-431" data-mp="12xi2syj2zqrp">
        {known ? <span className="cz-num" data-id="element-432" data-mp="ivdob44vki0g">{value}</span> : <span className="text-h4 font-medium text-ink-subtle" data-id="element-433" data-mp="iucgd8j5obmy">No data available</span>}
      </p>

      <p className="mt-2 text-caption leading-5 text-ink-muted" data-id="element-434" data-mp="etsgvyb40vxn">{meaning}</p>

      {known && series && series.length >= 3 && <div className={`mt-3 ${palette.text}`} data-id="element-435" data-mp="a07hc1tvbtwc">
          <Sparkline values={series} label={`${label} over the recorded period`} data-id="element-436" data-mp="52eenirrxi0t" />
        </div>}

      {known && <p className="mt-2 text-caption leading-5 text-ink-subtle" data-id="element-437" data-mp="5nw8oshyac8">
          {comparison ? <>
              <span aria-hidden="true" data-id="element-438" data-mp="3rcs39jon5wkn">
                {comparison.direction === 'up' ? '↑' : comparison.direction === 'down' ? '↓' : '→'}{' '}
              </span>
              {comparison.text}
            </> : 'No comparison period recorded'}
        </p>}

      {to && <Link to={to} className="mt-3 inline-flex min-h-touch items-center gap-1.5 text-body-sm font-semibold text-brand-700 hover:text-brand-800" data-id="element-439" data-mp="1gimgfhzdspbf">
          {linkLabel ?? 'Open'}
          <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-440" data-mp="1t0yj5yei95sm" />
        </Link>}
    </div>;
}

/**
 * Shape only, in `currentColor`, so it inherits the card's tone and both
 * themes. It carries no axis and no numbers, because a 60px-wide line cannot
 * be read as a value — the number above it is the value.
 */
function Sparkline({
  values,
  label
}: {
  values: number[];
  label: string;
}) {
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const points = values.map((value, index) => {
    const x = index / (values.length - 1) * 100;
    const y = 24 - (value - min) / span * 22;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(' ');
  return <svg viewBox="0 0 100 26" preserveAspectRatio="none" role="img" aria-label={label} className="h-6 w-full" data-id="element-441" data-mp="2qfucs8d71frw">
      <polyline points={points} fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" data-id="element-442" data-mp="2t1251m7u3p84" />
    </svg>;
}